import { LazyQueryResult, useLazyQuery } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { MessageChannel } from 'modules/graphql';
import { BaseOption } from 'ui/selects';

import { fetchMessageChannelTypes } from '../ModifySubscriptionEditor.queries';
import { parseChannelMessageTypesForMultiSelect } from './SubscriptionPicker.utils';

interface UseMultiSubscriptionDataValue {
  channelOptions: BaseOption<number>[];
  messageOptions: BaseOption<number>[];
  messageCategories: Record<string, string>;
  isLoading: boolean;
  search: (
    searchTerm: string,
  ) => Promise<ReturnType<typeof parseChannelMessageTypesForMultiSelect>>;
}

const UseMultiSubscriptionDataContext = createContext({} as UseMultiSubscriptionDataValue);

export const UseMultiSubscriptionDataProvider = ({ children }: { children: JSX.Element }) => {
  const [channelOptions, setChannelOptions] = useState<BaseOption<number>[]>([]);
  const [messageOptions, setMessageOptions] = useState<BaseOption<number>[]>([]);
  const [messageCategories, setMessageCategories] = useState<Record<string, string>>({});

  const [getChannelsAndMessages, { loading }] = useLazyQuery(fetchMessageChannelTypes, {
    variables: { search: '' },
    fetchPolicy: 'cache-and-network',
  });

  const search = useCallback(
    (searchTerm: string) =>
      getChannelsAndMessages({ variables: { search: searchTerm } }).then(
        ({
          data,
        }: LazyQueryResult<{ channelMessageTypes: MessageChannel[] }, { search: string }>) => {
          const { channelMessageTypes = [] } = data || {};
          return parseChannelMessageTypesForMultiSelect(channelMessageTypes);
        },
      ),
    [getChannelsAndMessages],
  );

  useEffect(() => {
    search('').then((parsedTypes) => {
      setChannelOptions(parsedTypes.channelOptions);
      setMessageOptions(parsedTypes.messageOptions);
      setMessageCategories(parsedTypes.messageCategories);
    });
  }, []);

  return (
    <UseMultiSubscriptionDataContext.Provider
      value={
        {
          channelOptions,
          messageOptions,
          messageCategories,
          isLoading: loading,
          search,
        } as UseMultiSubscriptionDataValue
      }
    >
      {children}
    </UseMultiSubscriptionDataContext.Provider>
  );
};

export const useMultiSubscriptionData = () => {
  const context = useContext(UseMultiSubscriptionDataContext);

  /* istanbul ignore next */
  if (isEmpty(context) && process.env.NODE_ENV === 'development') {
    throw new Error(
      'useMultiSubscriptionData must be used within the `UseMultiSubscriptionDataProvider` component',
    );
  }

  return context;
};
